import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useTheme } from '../../context/ThemeContext';
import { UsersIcon, BriefcaseIcon, ChatBubbleLeftRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { UsersIcon as UsersSolidIcon, BriefcaseIcon as BriefcaseSolidIcon, ChatBubbleLeftRightIcon as ChatBubbleLeftRightSolidIcon, Cog6ToothIcon as Cog6ToothSolidIcon } from '@heroicons/react/24/solid';
import { getInitials } from '../../utils/nameUtils';
import FeedbackButton from '../features/feedback/FeedbackButton';
import NotificationBadge from '../common/NotificationBadge';
import NotificationErrorBoundary from '../common/NotificationErrorBoundary';
import { useNotifications } from '../../hooks/useNotifications';
import ProfileCompletionBadge from '../common/ProfileCompletionBadge';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';

// List of admin user emails - in production, this should be stored in Firestore
const ADMIN_EMAILS = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];

const DesktopSidebar = () => {
  const { user, userProfile } = useAuthContext();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { counts, clearNotificationCount, refreshCountsDebounced, markPageVisited } = useNotifications();
  const { completionPercentage } = useProfileCompletion();
  

  // Check if current user is an admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // Handle navigation click with notification clearing and refresh
  const handleNavigationClick = (item) => {
    
    const pageTypeMap = {
      'Members': 'people',
      'Jobs': 'opportunities', 
      'Forum': 'forum'
    };
    
    const pageType = pageTypeMap[item.name];
    if (pageType) {
      // Clear notification count immediately for better UX
      if (item.notificationCount > 0) {
        clearNotificationCount(pageType);
        
        // Mark page as visited in database in background (non-blocking)
        markPageVisited(pageType).catch(error => {
          console.warn(`Failed to mark ${pageType} as visited on navigation:`, error);
        });
      }
    }
    
    // Refresh notification counts after navigation with debouncing
    refreshCountsDebounced(1000); // 1 second delay to allow page to load
    
  };

  // Navigation items configuration
  const navigation = [
    {
      name: 'Members',
      href: '/network',
      icon: UsersIcon,
      iconSolid: UsersSolidIcon,
      current: location.pathname === '/network',
      notificationCount: counts.people
    },
    {
      name: 'Jobs',
      href: '/jobs',
      icon: BriefcaseIcon,
      iconSolid: BriefcaseSolidIcon,
      current: location.pathname === '/jobs',
      notificationCount: counts.opportunities
    },
    {
      name: 'Forum',
      href: '/forum',
      icon: ChatBubbleLeftRightIcon,
      iconSolid: ChatBubbleLeftRightSolidIcon,
      current: location.pathname.startsWith('/forum'),
      notificationCount: counts.forum
    },
    // Admin button - only shown if user is admin
    ...(isAdmin ? [{
      name: 'Admin',
      href: '/admin',
      icon: Cog6ToothIcon,
      iconSolid: Cog6ToothSolidIcon,
      current: location.pathname === '/admin'
    }] : [])
  ];

  // Helper function to get navigation item classes
  const getNavItemClasses = (current) => {
    return current
      ? 'bg-indigo-100 dark:bg-purple-600/80 text-indigo-900 dark:text-white group flex items-center px-3 py-3 text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm dark:shadow-lg ring-1 ring-indigo-200 dark:ring-white/10 navigation-item'
      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-indigo-900 dark:hover:text-white hover:shadow-sm dark:hover:shadow-md group flex items-center px-3 py-3 text-sm font-semibold rounded-lg transition-all duration-200 navigation-item hover:ring-1 hover:ring-gray-200 dark:hover:ring-white/10';
  };

  // Helper function to get icon classes
  const getIconClasses = (current) => {
    return current
      ? 'text-indigo-700 dark:text-white mr-3 flex-shrink-0 h-6 w-6 transition-all duration-200 drop-shadow-sm'
      : 'text-gray-500 dark:text-gray-400 group-hover:text-indigo-700 dark:group-hover:text-white mr-3 flex-shrink-0 h-6 w-6 transition-all duration-200 group-hover:drop-shadow-sm';
  };

  return (
    <div className="fixed left-0 top-0 flex flex-col w-64 bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-950 min-h-screen shadow-lg dark:shadow-2xl border-r border-gray-200 dark:border-gray-700 transition-all duration-300 navigation-container z-40">
      {/* Logo section */}
      <div className="flex items-center flex-shrink-0 px-6 py-8 border-b border-gray-200 dark:border-gray-700/50">
        <Link to="/network" className="flex items-center group cursor-pointer transition-all duration-300 hover:scale-105">
          <div className="relative">
            <img
              className="h-8 w-auto transition-all duration-200 drop-shadow-sm group-hover:drop-shadow-md"
              src="/burnt-logo.svg"
              alt="Burnt"
              onError={(e) => {
                // Fallback to text if logo fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-purple-700/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 blur-sm -z-10"></div>
          </div>
          <span 
            className="ml-3 text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent transition-all duration-200 group-hover:from-purple-400 group-hover:to-purple-600"
            style={{ display: 'block' }}
          >
            burnt
          </span>
        </Link>
      </div>

      {/* Navigation section */}
      <div className="flex-1 flex flex-col">
        <nav className="flex-1 px-3 py-6 space-y-2">
          {navigation.map((item) => {
            const IconComponent = item.current ? item.iconSolid : item.icon;
            
            return (
              <button
                key={item.name}
                onClick={(e) => {
                  
                  e.preventDefault();
                  
                  // Handle notification clearing first
                  handleNavigationClick(item);
                  
                  // Navigate using React Router
                  navigate(item.href, { replace: true });
                }}
                className={`${getNavItemClasses(item.current)} w-full text-left`}
                aria-current={item.current ? 'page' : undefined}
                data-walkthrough-tab={item.name}
              >
                <IconComponent
                  className={getIconClasses(item.current)}
                  aria-hidden="true"
                />
                <span className="transition-all duration-200 flex-1">{item.name}</span>
                <NotificationErrorBoundary>
                  {item.notificationCount > 0 && (
                    <NotificationBadge 
                      count={item.notificationCount} 
                      className="ml-2" 
                    />
                  )}
                </NotificationErrorBoundary>
              </button>
            );
          })}
        </nav>

        {/* User profile section */}
        {user && (
          <div className="flex-shrink-0 flex-col p-4 transition-all duration-300">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="w-full mb-3 p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-indigo-900 dark:hover:text-white rounded-lg transition-all duration-200 hover:shadow-sm dark:hover:shadow-md hover:ring-1 hover:ring-gray-200 dark:hover:ring-white/10 group"
              title="Toggle theme"
            >
              <div className="flex items-center">
                {isDarkMode ? (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5 mr-3 transition-all duration-200 group-hover:drop-shadow-sm"
                  >
                    <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                  </svg>
                ) : (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5 mr-3 transition-all duration-200 group-hover:drop-shadow-sm"
                  >
                    <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="text-sm font-medium transition-all duration-200">
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </span>
              </div>
            </button>

            {/* Feedback Button */}
            <FeedbackButton variant="sidebar" />

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700/50 mb-3"></div>

            {/* User Profile Link */}
            <Link 
              to="/profile" 
              className="flex-shrink-0 w-full group block hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg p-3 -m-1 transition-all duration-200 hover:shadow-sm dark:hover:shadow-md hover:ring-1 hover:ring-gray-200 dark:hover:ring-white/10"
              aria-label="Edit my profile"
            >
              <div className="flex items-center">
                <div className="relative">
                  {(() => {
                    // Priority order for profile picture:
                    // 1. Custom uploaded profile picture from Firestore (userProfile.profilePicture)
                    // 2. Profile picture from basicInfo in Firestore (userProfile.basicInfo?.photoURL)
                    // 3. Firebase Auth profile picture (user.photoURL)
                    const profilePictureUrl = userProfile?.profilePicture || 
                                            userProfile?.basicInfo?.photoURL || 
                                            user.photoURL;
                    
                    return profilePictureUrl ? (
                      <img
                        className="inline-block h-10 w-10 rounded-full ring-2 ring-gray-300 dark:ring-white/30 transition-all duration-200 group-hover:ring-indigo-400 dark:group-hover:ring-white/50 group-hover:scale-105"
                        src={profilePictureUrl}
                        alt={user.displayName || user.email}
                      />
                    ) : (
                      <div className="inline-block h-10 w-10 rounded-full bg-indigo-500 dark:bg-purple-600 flex items-center justify-center ring-2 ring-gray-300 dark:ring-white/30 transition-all duration-200 group-hover:ring-indigo-400 dark:group-hover:ring-white/50 group-hover:scale-105 shadow-lg">
                        <span className="text-sm font-medium text-white">
                          {getInitials(user.displayName || user.email)}
                        </span>
                      </div>
                    );
                  })()}
                  
                  {/* Profile Completion Badge */}
                  <ProfileCompletionBadge
                    completionPercentage={completionPercentage}
                    variant="desktop"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/profile');
                    }}
                  />
                  
                  <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-400 rounded-full ring-2 ring-white dark:ring-gray-900 transition-all duration-200"></div>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-indigo-900 dark:group-hover:text-gray-200 transition-all duration-200 truncate">
                    {user.displayName || 'User'}
                  </p>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-indigo-700 dark:group-hover:text-gray-300 transition-all duration-200">
                    My Profile
                  </p>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopSidebar;